import { isEduPrefixedPath } from '../../config/appBase';

/**
 * Host redirects: sub-domains are retired and IVORI Edu lives at
 * https://ivoriai.com/edu/... (see config/appBase.js).
 *
 * - retiredEduHosts (edu.ivoriai.com, ivoriedu.com, ...): /X -> apex/X.
 * - The eight prototype exams' hosts send marketing paths straight to
 *   ivoriai.com/edu/<path> (no double hop). Their app paths stay (D-B).
 * - hiddenHosts (C-Suite): any path -> https://ivoriai.com/.
 * - mainHosts (ivoriai.com, www.ivoriai.com): ivoriai.com/ is the Edu home
 *   and stays; www. and ivori.ai send "/" there. The old Edu home address,
 *   /edu, goes to ivoriai.com/ so the home has one URL; every other Edu page
 *   keeps its /edu/... path. The company home lives at companyHomePath
 *   (/about). Any other path that reaches index.html is forwarded to
 *   /edu/<path> so old links (certificate /verify/<id>, emailed
 *   /login/callback) keep working.
 * - retiredPathbuddyHosts (pathbuddy.com, pathbuddy.app and their www.):
 *   Pathbuddy is retired. Its pages were at the root, so /X -> apex/X and
 *   "/" -> ivoriai.com/, like a retired edu host; /insights and
 *   /insights/<slug> go straight to their General insights page
 *   (pathbuddyInsights). Every pathbuddy host adds pathbuddyMarker to the
 *   query (redirectSearch) so the landing page can say Pathbuddy is now IVORI.
 * - The protected exam hosts are never redirected.
 *
 * The map lives in public/index.html as
 * <script id="ivori-host-redirects" type="application/json">.
 * The inline script in that file parses the same block. Keep the two
 * resolvers in step: this module is the React fallback when that script
 * did not run, and the tests drive this function with the HTML block.
 */

export function normalizePath(pathname) {
  const raw = (pathname || '/').split('?')[0].split('#')[0];
  if (!raw || raw === '/') return '/';
  const withSlash = raw.charAt(0) === '/' ? raw : `/${raw}`;
  if (withSlash.length > 1 && withSlash.charAt(withSlash.length - 1) === '/') {
    return withSlash.slice(0, -1);
  }
  return withSlash;
}

export function parseHostRedirectMap(html) {
  const match = String(html).match(
    /<script id="ivori-host-redirects" type="application\/json">([\s\S]*?)<\/script>/
  );
  if (!match) {
    throw new Error('ivori-host-redirects block is missing');
  }
  return JSON.parse(match[1]);
}

export function readHostRedirectMap(doc) {
  const root = doc || (typeof document !== 'undefined' ? document : null);
  if (!root || typeof root.getElementById !== 'function') return null;
  const el = root.getElementById('ivori-host-redirects');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || '');
  } catch (err) {
    return null;
  }
}

export function isProtectedExamHost(hostname, map) {
  const h = (hostname || '').toLowerCase();
  const needles = (map && map.protectedNeedles) || [];
  for (let i = 0; i < needles.length; i += 1) {
    const needle = needles[i];
    if (!needle) continue;
    if (
      h === needle
      || h.indexOf(`${needle}.`) === 0
      || h.indexOf(`.${needle}.`) !== -1
      || (h.length > needle.length && h.indexOf(`.${needle}`) === h.length - needle.length - 1)
    ) {
      return true;
    }
  }
  return false;
}

function matchExam(hostname, exams) {
  const h = hostname.toLowerCase();
  const list = exams || [];
  for (let i = 0; i < list.length; i += 1) {
    const exam = list[i];
    const needles = exam.hostNeedles || [];
    for (let n = 0; n < needles.length; n += 1) {
      if (needles[n] && h.indexOf(needles[n]) !== -1) return exam;
    }
  }
  return null;
}

function methodTarget(rest, map) {
  if (!rest || rest.indexOf('/') !== -1) return null;
  if (rest === 'pathbuddy') return '/method';
  const exams = map.exams || [];
  for (let i = 0; i < exams.length; i += 1) {
    const exam = exams[i];
    const legacy = exam.legacySlugs || [];
    const needles = exam.hostNeedles || [];
    if (exam.slug === rest || legacy.indexOf(rest) !== -1 || needles.indexOf(rest) !== -1) {
      return `/method/${exam.slug}`;
    }
  }
  return null;
}

export function marketingPathForExam(pathname, exam, map) {
  const path = normalizePath(pathname);
  if (path === '/') return exam.home;
  const pricing = map.pricingPaths || [];
  if (pricing.indexOf(path) !== -1) return path;

  const exams = map.exams || [];
  for (let i = 0; i < exams.length; i += 1) {
    const candidate = exams[i];
    const aliases = candidate.aliases || [];
    if (path === candidate.home || aliases.indexOf(path) !== -1) return candidate.home;
  }

  if (path === '/method') return `/method/${exam.slug}`;
  if (path.indexOf('/method/') === 0) {
    return methodTarget(path.slice('/method/'.length), map) || path;
  }

  if (path === '/insights') return `/insights/${exam.slug}`;
  if (path.indexOf('/insights/') === 0) {
    const parts = path.split('/').filter(Boolean);
    if (parts.length === 2) {
      const slug = parts[1];
      for (let i = 0; i < exams.length; i += 1) {
        if (exams[i].slug === slug) return `/insights/${exams[i].slug}`;
      }
      return `/insights/${exam.slug}/${slug}`;
    }
    return path;
  }

  const collapse = map.featureCollapse || [];
  if (collapse.indexOf(path) !== -1) return '/features';
  if (path === '/features' || path.indexOf('/features/') === 0) return path;

  const audiences = map.audiencePaths || [];
  if (audiences.indexOf(path) !== -1 || path.indexOf('/for-schools/') === 0) return path;

  if (path.indexOf('/case-studies/') === 0) {
    const slug = path.slice('/case-studies/'.length);
    if (!slug || slug.indexOf('/') !== -1) return null;
    const mapped = map.caseStudies && map.caseStudies[slug];
    return mapped || '/for-schools';
  }

  return null;
}

function inList(list, value) {
  return (list || []).indexOf(value) !== -1;
}

/** "/edu" (any case), the Edu home's old address; it is now ivoriai.com/. */
function isEduHomePath(map, path) {
  return String(path).toLowerCase() === map.eduBase;
}

/** An already-normalised path on the edu app: "/" and "/edu" are the Edu home. */
function eduTarget(map, path) {
  if (path === '/' || isEduHomePath(map, path)) return `${map.ivoriApex}/`;
  if (isEduPrefixedPath(path)) return `${map.ivoriApex}${path}`;
  return `${map.apex}${path}`;
}

function apexHost(map) {
  return String(map.ivoriApex).replace(/^https?:\/\//, '');
}

/** Target on the main domain; stayOnHost -> null when the URL is already right. */
function mainTarget(map, path, stayOnHost, hostname) {
  if (path === '/') return hostname === apexHost(map) ? null : `${map.ivoriApex}/`;
  if (isEduHomePath(map, path)) return `${map.ivoriApex}/`;
  if (path === map.companyHomePath || isEduPrefixedPath(path)) {
    return stayOnHost ? null : `${map.ivoriApex}${path}`;
  }
  return `${map.ivoriApex}${map.eduBase}${path}`;
}

/** Target for a retired Pathbuddy host, from an already-normalised path. */
function pathbuddyTarget(map, path) {
  if (path === '/insights') return `${map.apex}/insights/general`;
  if (path.indexOf('/insights/') === 0) {
    const slug = path.slice('/insights/'.length);
    if (slug.indexOf('/') === -1) {
      const moved = map.pathbuddyInsights || {};
      const to = Object.prototype.hasOwnProperty.call(moved, slug) ? moved[slug] : '/insights/general';
      return `${map.apex}${to}`;
    }
  }
  return eduTarget(map, path);
}

/**
 * The query string to send with a redirect away from this host: the page's
 * own search, plus pathbuddyMarker when the host is a Pathbuddy one.
 */
export function redirectSearch(hostname, search, map) {
  const s = search || '';
  const h = String(hostname || '').toLowerCase();
  if (!map || !map.pathbuddyMarker || h.indexOf('pathbuddy') === -1) return s;
  return s && s !== '?' ? `${s}&${map.pathbuddyMarker}` : `?${map.pathbuddyMarker}`;
}

/**
 * Absolute URL to send this host to, or null to stay.
 * Search and hash are not included; the caller appends them with
 * redirectSearch.
 * Pass the full browser path (window.location.pathname), not the
 * router-relative one: under the /edu basename the router path is "/chat"
 * and would be forwarded again.
 */
export function resolveHostRedirect(hostname, pathname, map) {
  if (!map || !hostname) return null;
  const h = String(hostname).toLowerCase();
  if (h.indexOf('localhost') !== -1 || h.indexOf('127.0.0.1') !== -1) return null;
  if (inList(map.retiredPathbuddyHosts, h)) return pathbuddyTarget(map, normalizePath(pathname));
  if (inList(map.hiddenHosts, h)) return `${map.ivoriApex}/`;
  if (isProtectedExamHost(h, map)) return null;
  if (inList(map.retiredEduHosts, h)) return eduTarget(map, normalizePath(pathname));
  if (h === 'ivori.ai' || h === 'www.ivori.ai') {
    return mainTarget(map, normalizePath(pathname), false, h);
  }
  if (inList(map.mainHosts, h)) return mainTarget(map, normalizePath(pathname), true, h);

  const exam = matchExam(h, map.exams);
  const legacy = h.indexOf('ivori.ai') !== -1
    || h.indexOf('ivoriedu.com') !== -1
    || (h.indexOf('pathbuddy') !== -1 && h.indexOf('pathbuddy.com') === -1);
  const current = Boolean(exam)
    && h.slice(-'.edu.ivoriai.com'.length) === '.edu.ivoriai.com'
    && h.indexOf('ivori.ai') === -1
    && h.indexOf('ivoriedu.com') === -1;
  if (!legacy && !current) return null;

  const mapped = exam ? marketingPathForExam(pathname, exam, map) : null;
  if (current) return mapped ? `${map.apex}${mapped}` : null;
  return mapped ? `${map.apex}${mapped}` : eduTarget(map, normalizePath(pathname));
}
